// src/views/tasks/EditTaskForm.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import formOptions from "../../config/formOptions.json";
import { graphqlEndpoint } from "../../config.js";
import { parseUKDate } from "../../utils/dateUtiles.js";
import { parse, isValid } from "date-fns";

import Header from "../../components/Header.jsx";
import MarkdownEditor from "../../components/MarkdownEditor";
import AdminUserSelect from "../../components/AdminUserSelect.jsx";
import LabelSelector from "../../components/LabelSelector.jsx";
import DateInput from "../../components/DateInput.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { READ_TASK_BY_ID } from "../../graph/tasks/queries.js";
import { GET_PROJECT_META } from "../../graph/projects/queries.js";
import {
  DELETE_TASK,
  UPDATE_TASK,
} from "../../graph/tasks/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);

const EditTaskForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [projects, setProjects] = useState([]);
  const location = useLocation();
  const { setSnackbar } = useSnackbar();
  const redirectPath = location.state?.redirectPath || "/manageTasks";

  const numOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? null
      : parseInt(v, 10);

       const floatOrNull = (v) =>
       v === "" || v === null || v === undefined
     ? null
     : Number.isNaN(Number(v))
     ? null
     : parseFloat(v);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await client.request(DELETE_TASK, { id: String(id) });
      setSnackbar({
        open: true,
        severity: "success",
        message: "Task deleted successfully",
      });
      navigate(redirectPath);
    } catch (err) {
      console.error("Delete failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete task",
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [{ readTaskById }, { readProjectsFilter }] = await Promise.all([
          client.request(READ_TASK_BY_ID, { id }),
          client.request(GET_PROJECT_META),
        ]);

        setProjects(readProjectsFilter);

        setInitialValues({
          title: readTaskById.title || "",
          description: readTaskById.description || "",
          status: readTaskById.status || "",
          labels: readTaskById.labels || [],
          assignedTo: readTaskById.assignedTo || "",
          dueDate:
            readTaskById.dueDate &&
            isValid(parse(readTaskById.dueDate, "dd-MM-yyyy", new Date()))
              ? readTaskById.dueDate
              : "",
          deferDate:
            readTaskById.deferDate &&
            isValid(parse(readTaskById.deferDate, "dd-MM-yyyy", new Date()))
              ? readTaskById.deferDate
              : "",
          cost: readTaskById.cost || "",
          projectId: readTaskById.projectId || "",
          // map mins to controlled empty string if nullish
          plannedDurationMin:
            readTaskById.plannedDurationMin ?? "",
          actualDurationMin:
            readTaskById.actualDurationMin ?? "",
        });
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [id]);

  const allowedLabels = formOptions?.labelOptions.map((l) => l.value) || [];

  const validationSchema = yup.object().shape({
    title: yup.string().required("required"),
    description: yup.string(),
    status: yup.string().required("required"),
    labels: yup
      .array()
      .of(yup.string().oneOf(allowedLabels, "Invalid label"))
      .min(1, "Select at least one label")
      .required("Select at least one label"),
    assignedTo: yup.string(),
    dueDate: yup.string(),
    deferDate: yup.string(),
    projectId: yup.string(),
    plannedDurationMin: yup
      .number()
      .typeError("Planned minutes must be a number")
      .integer("Use whole minutes")
      .min(0, "Must be ≥ 0")
      .nullable(),
    actualDurationMin: yup
      .number()
      .typeError("Actual minutes must be a number")
      .integer("Use whole minutes")
      .min(0, "Must be ≥ 0")
      .nullable(),
  });

  const handleFormSubmit = async (values) => {
    const cleanedValues = { ...values };

    for (const key of Object.keys(cleanedValues)) {
      if (cleanedValues[key] === "") cleanedValues[key] = null;
    }

    cleanedValues.dueDate = cleanedValues.dueDate
      ? parseUKDate(cleanedValues.dueDate)
      : null;
    cleanedValues.deferDate = cleanedValues.deferDate
      ? parseUKDate(cleanedValues.deferDate)
      : null;

    // coerce to Int or null
    cleanedValues.plannedDurationMin = numOrNull(
      cleanedValues.plannedDurationMin
    );
    cleanedValues.actualDurationMin = numOrNull(
      cleanedValues.actualDurationMin
    );

    cleanedValues.id = id;

    try {
      await client.request(UPDATE_TASK, {
        input: cleanedValues,
      });
      setSnackbar({
        open: true,
        severity: "success",
        message: "Task updated successfully",
      });
      navigate(redirectPath);
    } catch (err) {
      console.error("Update failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update task",
      });
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="EDIT TASK"
          subtitle={`Edit task: ${initialValues.title}`}
        />
        <Tooltip title="Delete">
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete Task
          </Button>
        </Tooltip>
      </Box>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              <LabelSelector
                selectedLabels={values.labels}
                setFieldValue={setFieldValue}
                error={errors.labels}
                touched={touched.labels}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {(formOptions.taskStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <AdminUserSelect
                selectedAdmin={values.assignedTo}
                setFieldValue={setFieldValue}
              />

              <DateInput
                label="Due Date"
                name="dueDate"
                value={values.dueDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.dueDate}
                touched={touched.dueDate}
              />

              <DateInput
                label="Defer Until"
                name="deferDate"
                value={values.deferDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.deferDate}
                touched={touched.deferDate}
              />

              {/* duration mins */}
              <TextField
                label="Planned minutes"
                name="plannedDurationMin"
                type="number"
                value={values.plannedDurationMin}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 1 }}
                error={!!touched.plannedDurationMin && !!errors.plannedDurationMin}
                helperText={touched.plannedDurationMin && errors.plannedDurationMin}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                label="Actual minutes"
                name="actualDurationMin"
                type="number"
                value={values.actualDurationMin}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 1 }}
                error={!!touched.actualDurationMin && !!errors.actualDurationMin}
                helperText={touched.actualDurationMin && errors.actualDurationMin}
                sx={{ gridColumn: "span 2" }}
              />
               <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Cost"
                name="cost"
                value={values.cost}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 0.01 }}
                error={!!touched.cost && !!errors.cost}
                helperText={touched.cost && errors.cost}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Project</InputLabel>
                <Select
                  name="projectId"
                  value={values.projectId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.projectId && !!errors.projectId}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button type="submit" color="secondary" variant="contained">
                Save Changes
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditTaskForm;
